import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Plus, FileText, Download, Trash2, Loader2, Receipt, Send, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { fmtEUR, fmtDate } from "../lib/format";

const STATUS_STYLES = {
  bozza: { label: "Bozza", cls: "bg-neutral-100 text-neutral-700 border-neutral-200" },
  inviato: { label: "Inviato", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  accettato: { label: "Accettato", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rifiutato: { label: "Rifiutato", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" },
};

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [q, c] = await Promise.all([api.get("/quotes"), api.get("/clients")]);
      setQuotes(q.data);
      setClients(c.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    const grand = quotes.reduce((s, q) => s + (q.total || 0), 0);
    const accepted = quotes.filter((q) => q.status === "accettato").reduce((s, q) => s + (q.total || 0), 0);
    return { grand, accepted };
  }, [quotes]);

  const openNew = () => {
    setEditing({
      client_id: clients[0]?.id || "",
      title: "",
      items: [{ description: "", quantity: 1, unit: "corpo", unit_price: 0, discount_percent: 0, vat_percent: 22 }],
      payment_terms: "30% all'incarico, 40% a metà lavori, 30% a saldo",
      notes: "",
    });
    setEditOpen(true);
  };

  const openEdit = (q) => { setEditing({ ...q }); setEditOpen(true); };

  const addItem = () => setEditing({ ...editing, items: [...(editing.items || []), { description: "", quantity: 1, unit: "corpo", unit_price: 0, discount_percent: 0, vat_percent: 22 }] });
  const updateItem = (i, k, v) => {
    const items = [...editing.items];
    items[i] = { ...items[i], [k]: k === "description" || k === "unit" ? v : (parseFloat(v) || 0) };
    setEditing({ ...editing, items });
  };
  const removeItem = (i) => setEditing({ ...editing, items: editing.items.filter((_, idx) => idx !== i) });

  const liveTotals = useMemo(() => {
    if (!editing?.items) return { subtotal: 0, vat: 0, total: 0 };
    let subtotal = 0, vat = 0;
    editing.items.forEach((it) => {
      const row = (it.quantity || 0) * (it.unit_price || 0) * (1 - (it.discount_percent || 0) / 100);
      subtotal += row;
      vat += row * (it.vat_percent || 0) / 100;
    });
    return { subtotal, vat, total: subtotal + vat };
  }, [editing]);

  const save = async () => {
    if (!editing.client_id) { toast.error("Cliente obbligatorio"); return; }
    if (!editing.title?.trim()) { toast.error("Titolo obbligatorio"); return; }
    try {
      if (editing.id) {
        await api.put(`/quotes/${editing.id}`, {
          title: editing.title, payment_terms: editing.payment_terms, notes: editing.notes, items: editing.items,
          issue_date: editing.issue_date, valid_until: editing.valid_until,
        });
      } else {
        await api.post("/quotes", editing);
      }
      toast.success("Salvato");
      setEditOpen(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const changeStatus = async (q, status) => {
    try { await api.put(`/quotes/${q.id}`, { status }); toast.success("Stato aggiornato"); await load(); }
    catch { toast.error("Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare il preventivo?")) return;
    await api.delete(`/quotes/${id}`);
    await load();
  };

  const downloadPdf = async (q) => {
    try {
      const t = localStorage.getItem("crm_token");
      const r = await fetch(`${API}/quotes/${q.id}/pdf`, { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) throw new Error("err");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${q.number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore download"); }
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="p-8 lg:p-10 space-y-6 fade-up">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="mono-label flex items-center gap-1.5"><Receipt className="h-3 w-3" /> Documenti commerciali</div>
          <h1 className="font-display text-4xl font-medium text-neutral-900 mt-1">Preventivi</h1>
          <p className="text-sm text-neutral-500 mt-2">
            Totale {quotes.length} preventivi · Valore <strong className="font-mono brand-text">{fmtEUR(totals.grand)}</strong>
            {totals.accepted > 0 && <> · Accettati <strong className="font-mono text-emerald-700">{fmtEUR(totals.accepted)}</strong></>}
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-quote-btn" className="brand-bg hover:opacity-90"><Plus className="h-4 w-4 mr-1.5" /> Nuovo preventivo</Button>
      </header>

      <div className="border border-neutral-200 bg-white">
        {quotes.length === 0 ? (
          <div className="p-10 text-center text-sm text-neutral-500">
            <FileText className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
            Ancora nessun preventivo. Crea il primo per inviarlo al cliente.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left">
                <th className="p-3 mono-label">Numero</th>
                <th className="p-3 mono-label">Oggetto</th>
                <th className="p-3 mono-label">Cliente</th>
                <th className="p-3 mono-label">Emesso</th>
                <th className="p-3 mono-label">Valido fino</th>
                <th className="p-3 mono-label text-right">Totale</th>
                <th className="p-3 mono-label">Stato</th>
                <th className="p-3 mono-label text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {quotes.map((q) => {
                const st = STATUS_STYLES[q.status] || STATUS_STYLES.bozza;
                return (
                  <tr key={q.id} className="hover:bg-neutral-50" data-testid={`quote-${q.id}`}>
                    <td className="p-3 font-mono text-xs">{q.number}</td>
                    <td className="p-3"><button onClick={() => openEdit(q)} className="text-left hover:underline">{q.title}</button></td>
                    <td className="p-3">{q.client_name}</td>
                    <td className="p-3 font-mono text-xs">{fmtDate(q.issue_date)}</td>
                    <td className="p-3 font-mono text-xs">{fmtDate(q.valid_until)}</td>
                    <td className="p-3 text-right font-mono">{fmtEUR(q.total)}</td>
                    <td className="p-3">
                      <select
                        value={q.status}
                        onChange={(e) => changeStatus(q, e.target.value)}
                        data-testid={`quote-status-${q.id}`}
                        className={`font-mono text-[10px] font-semibold px-1.5 py-1 border ${st.cls}`}
                      >
                        <option value="bozza">Bozza</option>
                        <option value="inviato">Inviato</option>
                        <option value="accettato">Accettato</option>
                        <option value="rifiutato">Rifiutato</option>
                      </select>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => downloadPdf(q)} data-testid={`pdf-${q.id}`} title="Scarica PDF" className="p-1.5 hover:bg-neutral-100"><Download className="h-3.5 w-3.5" /></button>
                        <button onClick={() => remove(q.id)} data-testid={`del-quote-${q.id}`} title="Elimina" className="p-1.5 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit/Create dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? `Preventivo ${editing.number}` : "Nuovo preventivo"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label">Cliente *</label>
                  <select value={editing.client_id || ""} onChange={(e) => setEditing({ ...editing, client_id: e.target.value })} data-testid="quote-client" className="w-full border border-neutral-300 px-3 py-2 text-sm" disabled={!!editing.id}>
                    <option value="">— scegli —</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mono-label">Oggetto *</label>
                  <Input value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="quote-title" placeholder="es. Preventivo progettazione villa Rossi" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="mono-label">Data emissione</label>
                  <Input type="date" value={editing.issue_date ? editing.issue_date.slice(0, 10) : ""} onChange={(e) => setEditing({ ...editing, issue_date: e.target.value })} />
                </div>
                <div>
                  <label className="mono-label">Valido fino</label>
                  <Input type="date" value={editing.valid_until ? editing.valid_until.slice(0, 10) : ""} onChange={(e) => setEditing({ ...editing, valid_until: e.target.value })} />
                </div>
                {editing.id && (
                  <div>
                    <label className="mono-label">Numero</label>
                    <div className="font-mono text-sm py-2 px-3 border border-neutral-200 bg-neutral-50">{editing.number}</div>
                  </div>
                )}
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between">
                  <label className="mono-label">Voci</label>
                  <button type="button" onClick={addItem} data-testid="add-item" className="text-xs flex items-center gap-1 brand-text hover:underline"><Plus className="h-3 w-3" /> Aggiungi voce</button>
                </div>
                <div className="border border-neutral-200 mt-1 divide-y divide-neutral-100">
                  <div className="grid grid-cols-12 gap-2 p-2 bg-neutral-50 mono-label text-[10px]">
                    <div className="col-span-5">Descrizione</div>
                    <div className="col-span-1">Q.tà</div>
                    <div className="col-span-1">Unità</div>
                    <div className="col-span-2">Prezzo €</div>
                    <div className="col-span-1">Sc. %</div>
                    <div className="col-span-1">IVA %</div>
                    <div className="col-span-1 text-right">—</div>
                  </div>
                  {(editing.items || []).map((it, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 p-2 items-center" data-testid={`item-${i}`}>
                      <Input className="col-span-5" value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} placeholder="Descrizione voce" />
                      <Input className="col-span-1" type="number" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} />
                      <Input className="col-span-1" value={it.unit || ""} onChange={(e) => updateItem(i, "unit", e.target.value)} placeholder="corpo" />
                      <Input className="col-span-2" type="number" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} />
                      <Input className="col-span-1" type="number" value={it.discount_percent} onChange={(e) => updateItem(i, "discount_percent", e.target.value)} />
                      <Input className="col-span-1" type="number" value={it.vat_percent} onChange={(e) => updateItem(i, "vat_percent", e.target.value)} />
                      <button type="button" onClick={() => removeItem(i)} data-testid={`del-item-${i}`} className="col-span-1 justify-self-end p-1.5 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-6 mt-3 text-sm">
                  <div><span className="mono-label">Imponibile</span> <span className="font-mono ml-1">{fmtEUR(liveTotals.subtotal)}</span></div>
                  <div><span className="mono-label">IVA</span> <span className="font-mono ml-1">{fmtEUR(liveTotals.vat)}</span></div>
                  <div><span className="mono-label">Totale</span> <span className="font-mono ml-1 brand-text font-semibold">{fmtEUR(liveTotals.total)}</span></div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label">Modalità di pagamento</label>
                  <Textarea rows={2} value={editing.payment_terms || ""} onChange={(e) => setEditing({ ...editing, payment_terms: e.target.value })} />
                </div>
                <div>
                  <label className="mono-label">Note</label>
                  <Textarea rows={2} value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-quote" className="brand-bg hover:opacity-90">Salva preventivo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
